// Spoke Growth — animated hub/spoke map of referrals over time.
//
// User picks a scope (facility, customer, or saved filter), date range,
// and direction (inbound / outbound / both). We build a sorted list of
// referral "events" for spokes outside the hub set, then animate their
// appearance on a Google Map. Repeat referrals from the same spoke
// brighten it along a colormap. Play / pause / scrub / speed controls
// drive the timeline; Export MP4 renders a video of the same animation.
//
// Non-VE spokes: the timeline data model already tags each event with
// its facility source ('ve' vs 'non-ve'); we currently only receive
// 've' events until the CTO ships the non-VE physician API. The plumbing
// is ready to accept 'non-ve' events without further UI changes.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, InfoWindow, MarkerF, PolylineF, useJsApiLoader } from '@react-google-maps/api';
import { useCache } from '../../hooks/useCache';
import { useCustomerFilter } from '../../hooks/useCustomerFilter';
import { buildAddressString, geocodeFacilities } from '../../utils/geocodeCache';
import { isTestRecord } from '../../utils/testDataFilter';
import {
  DIRECTION_INBOUND,
  DIRECTION_OUTBOUND,
  DIRECTION_EITHER,
} from '../../utils/referralDirection';
import {
  buildSpokeGrowthTimeline,
  advanceStateToTime,
} from '../../utils/spokeGrowthTimeline';
import {
  COLORMAPS,
  DEFAULT_COLORMAP_ID,
  sampleColormap,
  spokeMarkerSize,
  lerpHex,
} from '../../utils/spokeGrowthColormap';
import { exportSpokeGrowthMp4 } from '../../utils/spokeGrowthMp4Export';
import './SpokeGrowth.css';

const MAP_LIBRARIES = ['places'];
const MAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const MAP_CONTAINER = { width: '100%', height: '640px' };
const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_ZOOM = 4;

// Playback speed options, expressed as target seconds for the whole
// animation. Small numbers = fast overview; large numbers = detailed
// walkthrough. We keep the list short because most users just want
// "quick", "medium", or "slow".
const SPEED_OPTIONS = [
  { id: 'fast',   label: 'Fast (15s)',    seconds: 15 },
  { id: 'medium', label: 'Medium (30s)',  seconds: 30 },
  { id: 'slow',   label: 'Slow (60s)',    seconds: 60 },
  { id: 'xslow',  label: 'Very slow (120s)', seconds: 120 },
];

const DIRECTION_OPTIONS_LOCAL = [
  { value: DIRECTION_INBOUND,  label: 'Inbound (spokes send TO hubs)' },
  { value: DIRECTION_OUTBOUND, label: 'Outbound (spokes receive FROM hubs)' },
  { value: DIRECTION_EITHER,   label: 'Both directions' },
];

const SCOPE_KINDS = [
  { value: 'customer', label: 'Customer (one or more hub facilities)' },
  { value: 'filter',   label: 'Saved Filter' },
  { value: 'facility', label: 'Single Facility' },
];

// Map presets. Each preset combines a `mapTypeId` and a `styles` array
// controlling what geometry / labels are drawn. The recipe here is:
//   - `standard` / `terrain` / `satellite` use Google's built-in tiles.
//   - `muted` desaturates + lightens so the dots become the focal point.
//   - `faint` is nearly white — same idea, cranked further.
//   - `dark` inverts to a low-contrast night look; hot spokes really pop.
//   - `blank` hides every feature. Useful when you want ONLY the dots.
//
// Static Maps API supports the same `styles` array via repeated `style=`
// params, so we can mirror any of these into the MP4 export. See
// spokeGrowthMp4Export.encodeStaticStyle().
const MAP_STYLES = [
  {
    id: 'standard',
    label: 'Standard',
    mapTypeId: 'roadmap',
    styles: [],
  },
  {
    id: 'terrain',
    label: 'Terrain',
    mapTypeId: 'terrain',
    styles: [],
  },
  {
    id: 'satellite',
    label: 'Satellite',
    mapTypeId: 'hybrid',
    styles: [],
  },
  {
    id: 'muted',
    label: 'Muted (desaturated)',
    mapTypeId: 'roadmap',
    styles: [
      { elementType: 'geometry', stylers: [{ saturation: -80 }, { lightness: 20 }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#9ca3af' }] },
      { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },
      { featureType: 'poi', stylers: [{ visibility: 'off' }] },
      { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      { featureType: 'road.local', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    ],
  },
  {
    id: 'faint',
    label: 'Faint (dots pop)',
    mapTypeId: 'roadmap',
    styles: [
      { elementType: 'geometry', stylers: [{ color: '#f8fafc' }] },
      { elementType: 'labels', stylers: [{ visibility: 'off' }] },
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#e2e8f0' }] },
      { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#f1f5f9' }] },
      { featureType: 'road', stylers: [{ visibility: 'off' }] },
      { featureType: 'poi', stylers: [{ visibility: 'off' }] },
      { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#cbd5e1' }, { weight: 0.6 }] },
    ],
  },
  {
    id: 'dark',
    label: 'Dark',
    mapTypeId: 'roadmap',
    styles: [
      { elementType: 'geometry', stylers: [{ color: '#1f2937' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#9ca3af' }] },
      { elementType: 'labels.text.stroke', stylers: [{ color: '#111827' }] },
      { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#374151' }] },
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0f172a' }] },
      { featureType: 'poi', stylers: [{ visibility: 'off' }] },
      { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#374151' }] },
    ],
  },
  {
    id: 'blank',
    label: 'Blank (map off)',
    mapTypeId: 'roadmap',
    // Hide everything so only the map background shows through. The div
    // background CSS gives us a solid neutral canvas below the markers.
    styles: [{ stylers: [{ visibility: 'off' }] }],
  },
];
const DEFAULT_MAP_STYLE_ID = 'standard';

// Connection-line rendering modes. "flyin" grows each spoke→hub segment
// over ~0.7s of playback time so new referrals look like they shoot in.
const LINE_EFFECTS = [
  { id: 'icons',  label: 'Only spoke icons' },
  { id: 'static', label: 'Lines appear' },
  { id: 'flyin',  label: 'Lines fly in' },
];
const DEFAULT_LINE_EFFECT = 'icons';
const FLY_IN_DURATION_MS = 700;

// How spoke→hub connection lines are colored (independent of marker colormap).
const LINE_COLOR_MODES = [
  { id: 'black',    label: 'Black (all lines)' },
  { id: 'colormap', label: 'Follow colormap' },
  { id: 'custom',   label: 'Custom color…' },
];
const DEFAULT_LINE_COLOR_MODE = 'black';
const DEFAULT_CUSTOM_LINE_COLOR = '#000000';
// Brief size+color flash when a referral hits a spoke (playback ms).
// Keep this short: high-volume spokes update lastT often, and a long
// window left them stuck in the pop color (looked like "everything is cyan"
// on the heatmap). Magenta stands out on every bundled colormap including
// heatmap's blue→cyan→green→red ramp.
const POP_DURATION_MS = 450;
const POP_COLOR = '#ff00aa';
const POP_STROKE = '#ffffff';

// Human-readable defaults for the date pickers: last 12 months.
function defaultDates() {
  const end = new Date();
  const start = new Date();
  start.setFullYear(start.getFullYear() - 1);
  return {
    startDate: fmtDate(start),
    endDate: fmtDate(end),
  };
}
function fmtDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function fmtDateLabel(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function SpokeGrowth() {
  const cache = useCache();
  const { customers, resolveCustomerFacilityIds } = useCustomerFilter();

  // ---- Scope + filter state --------------------------------------------
  const [scopeKind, setScopeKind] = useState('customer');
  const [customerId, setCustomerId] = useState('');
  const [savedFilters, setSavedFilters] = useState([]);
  const [filterId, setFilterId] = useState('');
  const [facilityHierarchy, setFacilityHierarchy] = useState([]);
  const [flatFacilities, setFlatFacilities] = useState([]);
  const [singleFacilityId, setSingleFacilityId] = useState('');
  const [facilitySearch, setFacilitySearch] = useState('');

  const { startDate: defStart, endDate: defEnd } = defaultDates();
  const [startDate, setStartDate] = useState(defStart);
  const [endDate, setEndDate] = useState(defEnd);
  const [direction, setDirection] = useState(DIRECTION_INBOUND);
  const [speedId, setSpeedId] = useState('medium');
  const [colormapId, setColormapId] = useState(DEFAULT_COLORMAP_ID);
  const [removeTestData, setRemoveTestData] = useState(true);
  const [mapStyleId, setMapStyleId] = useState(DEFAULT_MAP_STYLE_ID);
  const [lineEffect, setLineEffect] = useState(DEFAULT_LINE_EFFECT);
  const [lineColorMode, setLineColorMode] = useState(DEFAULT_LINE_COLOR_MODE);
  const [customLineColor, setCustomLineColor] = useState(DEFAULT_CUSTOM_LINE_COLOR);
  const [popOnHit, setPopOnHit] = useState(true);

  // ---- Data + timeline state -------------------------------------------
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [timeline, setTimeline] = useState(null);
  const [hasBuilt, setHasBuilt] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);
  const [exportMessage, setExportMessage] = useState('');

  // ---- Playback state --------------------------------------------------
  // playhead is in [0,1] over the compressed animation duration.
  const [playhead, setPlayhead] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const rafRef = useRef(null);
  const lastFrameTimeRef = useRef(null);

  // ---- Google Maps loader ----------------------------------------------
  const { isLoaded: mapsLoaded, loadError: mapsLoadError } = useJsApiLoader({
    googleMapsApiKey: MAP_KEY,
    libraries: MAP_LIBRARIES,
  });

  // ---- Bootstrap -------------------------------------------------------
  useEffect(() => {
    (async () => {
      try {
        const [hier, filters, facs] = await Promise.all([
          cache.getCachedFacilityHierarchy(),
          cache.getSavedFilters(),
          cache.getCachedFacilities(),
        ]);
        if (hier?.success) setFacilityHierarchy(hier.hierarchy || []);
        if (filters?.filters) setSavedFilters(filters.filters);
        if (facs?.success) setFlatFacilities(facs.facilities || []);
      } catch {
        /* facilities/filters may not be cached yet; page still usable
           with a manual date range once data is refreshed elsewhere. */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Facility coord map (from SF hierarchy) --------------------------
  const facilityCoordsByVeId = useMemo(() => {
    const out = new Map();
    // Recurse the SF hierarchy which is the richer source; flat list is
    // a fallback for facilities not visible to SF but present in our
    // cached facilities table (rare but possible for older customers).
    const walk = (node) => {
      const veIdRaw =
        node?.vitalEngine_Facility_ID__c ??
        node?.vitalEngineFacilityId ??
        node?.VitalEngine_Facility_ID__c ??
        null;
      if (veIdRaw != null && veIdRaw !== '') {
        const canonical = Math.round(parseFloat(String(veIdRaw))).toString();
        if (!out.has(canonical)) {
          const latRaw = node.billingLatitude ?? node.BillingLatitude;
          const lngRaw = node.billingLongitude ?? node.BillingLongitude;
          const lat = latRaw != null && latRaw !== '' ? Number(latRaw) : null;
          const lng = lngRaw != null && lngRaw !== '' ? Number(lngRaw) : null;
          out.set(canonical, {
            veId: canonical,
            name: node.label || node.name || `Facility ${canonical}`,
            lat: Number.isFinite(lat) ? lat : null,
            lng: Number.isFinite(lng) ? lng : null,
            street: node.billingStreet || node.BillingStreet || null,
            city: node.billingCity || node.BillingCity || null,
            state: node.billingState || node.BillingState || null,
            postalCode: node.billingPostalCode || node.BillingPostalCode || null,
            country: node.billingCountry || node.BillingCountry || null,
          });
        }
      }
      (node.children || []).forEach(walk);
    };
    (facilityHierarchy || []).forEach(walk);
    // Fill from flat facilities table for anything the hierarchy missed.
    (flatFacilities || []).forEach(f => {
      const veIdRaw =
        f?.vitalEngine_Facility_ID__c ??
        f?.vitalEngineFacilityId ??
        null;
      if (veIdRaw == null || veIdRaw === '') return;
      const canonical = Math.round(parseFloat(String(veIdRaw))).toString();
      if (out.has(canonical)) return;
      const lat = f.billingLatitude != null ? Number(f.billingLatitude) : null;
      const lng = f.billingLongitude != null ? Number(f.billingLongitude) : null;
      out.set(canonical, {
        veId: canonical,
        name: f.name || `Facility ${canonical}`,
        lat: Number.isFinite(lat) ? lat : null,
        lng: Number.isFinite(lng) ? lng : null,
        street: f.billingStreet || null,
        city: f.billingCity || null,
        state: f.billingState || null,
        postalCode: f.billingPostalCode || null,
        country: f.billingCountry || null,
      });
    });
    return out;
  }, [facilityHierarchy, flatFacilities]);

  // ---- Resolve hub facility ids from the picked scope ------------------
  const [hubFacilityIds, setHubFacilityIds] = useState(new Set());
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (scopeKind === 'customer' && customerId) {
        const ids = await resolveCustomerFacilityIds(customerId);
        if (!cancelled) setHubFacilityIds(new Set(ids.map(String)));
      } else if (scopeKind === 'filter' && filterId) {
        const sf = savedFilters.find(s => String(s.id) === String(filterId));
        if (!cancelled) setHubFacilityIds(new Set((sf?.facilityIds || []).map(String)));
      } else if (scopeKind === 'facility' && singleFacilityId) {
        if (!cancelled) setHubFacilityIds(new Set([String(singleFacilityId)]));
      } else if (!cancelled) {
        setHubFacilityIds(new Set());
      }
    })();
    return () => { cancelled = true; };
  }, [scopeKind, customerId, filterId, savedFilters, singleFacilityId, resolveCustomerFacilityIds]);

  const canBuild = hubFacilityIds.size > 0 && startDate && endDate;

  // ---- Facility list for the single-facility picker --------------------
  const facilityPickerRows = useMemo(() => {
    // The flat facilities endpoint gives us name + VE id which is all
    // the single-facility picker needs. We add a light search filter so
    // customers with hundreds of facilities are still browsable.
    const q = facilitySearch.trim().toLowerCase();
    const rows = (flatFacilities || [])
      .map(f => ({
        veId:
          f.vitalEngine_Facility_ID__c != null
            ? Math.round(parseFloat(String(f.vitalEngine_Facility_ID__c))).toString()
            : null,
        name: f.name || '(unnamed)',
        city: f.billingCity || '',
        state: f.billingState || '',
      }))
      .filter(f => f.veId != null);
    rows.sort((a, b) => a.name.localeCompare(b.name));
    if (!q) return rows.slice(0, 300);
    return rows.filter(f => (
      f.name.toLowerCase().includes(q)
      || String(f.veId).includes(q)
      || f.city.toLowerCase().includes(q)
      || f.state.toLowerCase().includes(q)
    )).slice(0, 300);
  }, [flatFacilities, facilitySearch]);

  // ---- Build timeline: fetch referrals + aggregate ---------------------
  const buildTimeline = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    setTimeline(null);
    setPlayhead(0);
    setIsPlaying(false);
    setHasBuilt(false);
    try {
      const res = await cache.getCachedData('referrals', startDate, endDate);
      if (!res?.success) throw new Error('Failed to load cached referrals');
      const records = res.records || [];
      const filtered = removeTestData ? records.filter(r => !isTestRecord(r)) : records;

      const built = buildSpokeGrowthTimeline({
        records: filtered,
        hubFacilityIds,
        direction,
        facilityCoordsByVeId,
        startDate,
        endDate,
      });
      setTimeline(built);
      setHasBuilt(true);
    } catch (e) {
      setLoadError(e?.message || 'Failed to build timeline.');
    } finally {
      setLoading(false);
    }
  }, [cache, startDate, endDate, removeTestData, hubFacilityIds, direction, facilityCoordsByVeId]);

  // ---- Client-side geocode for spokes/hubs missing lat/lng -------------
  const [resolvedCoords, setResolvedCoords] = useState({});
  const [geocoding, setGeocoding] = useState(false);

  // Memoize the missing-coord lists so the deps below get a stable
  // identity across renders — otherwise `timeline?.x || []` creates a
  // new array literal each render and thrashes the downstream memos.
  const spokesMissingCoords = useMemo(
    () => timeline?.spokesMissingCoords || [],
    [timeline]
  );
  const hubsMissingCoords = useMemo(
    () => timeline?.hubsMissingCoords || [],
    [timeline]
  );

  const needGeocodeKey = useMemo(() => {
    const list = [...(hubsMissingCoords || []), ...(spokesMissingCoords || [])]
      .map(f => `${f.veId}|${buildAddressString(f)}`);
    return list.join('||');
  }, [hubsMissingCoords, spokesMissingCoords]);

  useEffect(() => {
    if (!mapsLoaded || !needGeocodeKey) return;
    let cancelled = false;
    setGeocoding(true);
    (async () => {
      const seen = new Set();
      const targets = [];
      for (const f of [...hubsMissingCoords, ...spokesMissingCoords]) {
        if (!f?.veId || seen.has(f.veId)) continue;
        const addr = buildAddressString(f);
        if (!addr) continue;
        seen.add(f.veId);
        targets.push({ ...f, _address: addr });
      }
      const resolved = await geocodeFacilities(targets);
      if (!cancelled && Object.keys(resolved).length > 0) {
        setResolvedCoords(prev => ({ ...prev, ...resolved }));
      }
      if (!cancelled) setGeocoding(false);
    })();
    return () => { cancelled = true; };
  }, [mapsLoaded, needGeocodeKey, hubsMissingCoords, spokesMissingCoords]);

  // ---- Effective spoke/hub sets after fallback geocode -----------------
  const displayHubs = useMemo(() => {
    if (!timeline) return [];
    const extras = (timeline.hubsMissingCoords || [])
      .map(f => {
        const c = resolvedCoords[f.veId];
        return c ? { ...f, lat: c.lat, lng: c.lng } : null;
      })
      .filter(Boolean);
    return [...timeline.hubs, ...extras];
  }, [timeline, resolvedCoords]);

  const displaySpokesAll = useMemo(() => {
    if (!timeline) return [];
    const extras = (timeline.spokesMissingCoords || [])
      .map(f => {
        const c = resolvedCoords[f.veId];
        return c ? { ...f, lat: c.lat, lng: c.lng } : null;
      })
      .filter(Boolean);
    return [...timeline.spokes, ...extras];
  }, [timeline, resolvedCoords]);

  // Merge display metadata (coords + name) into the timeline events so
  // the render loop and InfoWindow don't have to keep chasing lookups.
  // Events with no coords (even after client geocode) are dropped
  // rather than plotted at (null, null).
  const eventsWithCoords = useMemo(() => {
    if (!timeline) return [];
    const spokeById = new Map(displaySpokesAll.map(s => [String(s.veId), s]));
    return (timeline.events || [])
      .map(ev => {
        const s = spokeById.get(String(ev.spokeVeId));
        if (!s || s.lat == null || s.lng == null) return null;
        return { ...ev, lat: s.lat, lng: s.lng, name: s.name };
      })
      .filter(Boolean);
  }, [timeline, displaySpokesAll]);

  // ---- Timeline compression -------------------------------------------
  const speed = SPEED_OPTIONS.find(s => s.id === speedId) || SPEED_OPTIONS[1];
  const animationDurationMs = speed.seconds * 1000;

  // ---- Playback tick ---------------------------------------------------
  useEffect(() => {
    if (!isPlaying || !timeline) return undefined;
    lastFrameTimeRef.current = null;
    const tick = (now) => {
      if (lastFrameTimeRef.current == null) lastFrameTimeRef.current = now;
      const dt = now - lastFrameTimeRef.current;
      lastFrameTimeRef.current = now;
      setPlayhead(prev => {
        const next = prev + dt / animationDurationMs;
        if (next >= 1) {
          setIsPlaying(false);
          return 1;
        }
        return next;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isPlaying, animationDurationMs, timeline]);

  // ---- Playback derived state -----------------------------------------
  const renderState = useMemo(() => {
    if (!timeline || eventsWithCoords.length === 0) {
      return { visibleSpokes: [], currentTimestamp: null, cumulative: {} };
    }
    return advanceStateToTime({
      events: eventsWithCoords,
      playhead,
      startMs: timeline.startMs,
      endMs: timeline.endMs,
    });
  }, [timeline, eventsWithCoords, playhead]);

  const currentTimestampLabel = useMemo(() => {
    if (!timeline) return '';
    if (renderState.currentTimestamp == null) return fmtDateLabel(startDate);
    return fmtDateLabel(new Date(renderState.currentTimestamp).toISOString());
  }, [renderState, timeline, startDate]);

  // ---- Colormap sampler for the current colormap ---------------------
  const colormapMax = useMemo(() => {
    let m = 0;
    for (const s of renderState.visibleSpokes) {
      if (s.count > m) m = s.count;
    }
    return Math.max(m, 1);
  }, [renderState]);

  const spokeColorForCount = useCallback((count) => {
    return sampleColormap(colormapId, count, colormapMax);
  }, [colormapId, colormapMax]);

  // Line stroke color is independent of marker fills. Default black keeps
  // the map readable; colormap/custom are opt-in for flashier exports.
  const lineColorForCount = useCallback((count) => {
    if (lineColorMode === 'colormap') return spokeColorForCount(count);
    if (lineColorMode === 'custom') return customLineColor || DEFAULT_CUSTOM_LINE_COLOR;
    return '#000000';
  }, [lineColorMode, customLineColor, spokeColorForCount]);

  // ---- Hub-to-spoke connection lines (optional) -----------------------
  // Straight-line lat/lng between each visible spoke and its nearest hub.
  // Cheap L2 distance is fine — hubs are a small set (usually 1–few) and
  // the metric only needs to pick a visual anchor, not a real routing.
  const hubsWithCoords = useMemo(
    () => displayHubs.filter(h => h.lat != null && h.lng != null),
    [displayHubs]
  );
  const nearestHubFor = useCallback((lat, lng) => {
    if (hubsWithCoords.length === 0) return null;
    if (hubsWithCoords.length === 1) return hubsWithCoords[0];
    let best = hubsWithCoords[0];
    let bestD = Infinity;
    for (const h of hubsWithCoords) {
      const dLat = h.lat - lat;
      const dLng = h.lng - lng;
      const d = dLat * dLat + dLng * dLng;
      if (d < bestD) { bestD = d; best = h; }
    }
    return best;
  }, [hubsWithCoords]);

  // For each visible spoke, precompute { path, color } for the polyline.
  // flyin: the tip starts at the spoke and grows toward the hub over
  // ~FLY_IN_DURATION_MS of playback, eased so it feels like a shot in.
  const connectionLines = useMemo(() => {
    if (lineEffect === 'icons' || hubsWithCoords.length === 0 || !timeline) return [];
    const rangeMs = Math.max(1, timeline.endMs - timeline.startMs);
    const flyWindow = Math.min(0.12, Math.max(0.012, FLY_IN_DURATION_MS / animationDurationMs));
    // Solid black/custom reads better a bit more opaque than colormap lines.
    const baseOpacity = lineColorMode === 'colormap' ? 0.55 : 0.8;
    return renderState.visibleSpokes
      .map(sp => {
        if (sp.lat == null || sp.lng == null) return null;
        const hub = nearestHubFor(sp.lat, sp.lng);
        if (!hub) return null;

        let progress = 1;
        if (lineEffect === 'flyin') {
          const firstT = sp.firstT != null ? sp.firstT : timeline.startMs;
          const spokeFirstPlayhead = (firstT - timeline.startMs) / rangeMs;
          const raw = (playhead - spokeFirstPlayhead) / flyWindow;
          const clamped = Math.max(0, Math.min(1, raw));
          // Ease-out cubic: fast launch, soft landing at the hub.
          progress = 1 - Math.pow(1 - clamped, 3);
        }
        if (progress <= 0.001) return null;

        const tipLat = sp.lat + (hub.lat - sp.lat) * progress;
        const tipLng = sp.lng + (hub.lng - sp.lng) * progress;
        return {
          key: `line-${sp.veId}-${hub.veId}`,
          path: [
            { lat: sp.lat, lng: sp.lng },
            { lat: tipLat, lng: tipLng },
          ],
          color: lineColorForCount(sp.count),
          count: sp.count,
          // Slightly brighter while still flying in.
          opacity: lineEffect === 'flyin' && progress < 1
            ? Math.min(0.95, baseOpacity + 0.2)
            : baseOpacity,
        };
      })
      .filter(Boolean);
  }, [
    lineEffect,
    lineColorMode,
    hubsWithCoords,
    timeline,
    animationDurationMs,
    playhead,
    renderState.visibleSpokes,
    nearestHubFor,
    lineColorForCount,
  ]);

  // ---- Active map style options ---------------------------------------
  const activeMapStyle = useMemo(
    () => MAP_STYLES.find(s => s.id === mapStyleId) || MAP_STYLES[0],
    [mapStyleId]
  );

  // ---- Map bounds fit --------------------------------------------------
  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const boundsKey = useMemo(() => {
    const h = displayHubs.map(p => `H${p.veId}`).join('|');
    const s = displaySpokesAll.map(p => `S${p.veId}`).join('|');
    return `${h}#${s}`;
  }, [displayHubs, displaySpokesAll]);
  useEffect(() => {
    if (!mapReady || !mapRef.current || !window.google?.maps) return;
    const all = [...displayHubs, ...displaySpokesAll];
    if (all.length === 0) return;
    if (all.length === 1) {
      mapRef.current.setCenter({ lat: all[0].lat, lng: all[0].lng });
      mapRef.current.setZoom(11);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    all.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
    mapRef.current.fitBounds(bounds, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundsKey, mapReady]);

  // ---- Info window state ----------------------------------------------
  const [activeSpokeId, setActiveSpokeId] = useState(null);
  const [activeHubId, setActiveHubId] = useState(null);
  const formatFacilityAddress = useCallback((f) => {
    if (!f) return '';
    const street = f.street && String(f.street).trim();
    const city = f.city && String(f.city).trim();
    const state = f.state && String(f.state).trim();
    const zip = f.postalCode && String(f.postalCode).trim();
    // Full-ish address.
    if (street || city) {
      return [street, city, state, zip].filter(Boolean).join(', ');
    }
    // State-only (common SF gap) — don't pretend it's a real address.
    if (state) return `(incomplete in Salesforce — ${state} only)`;
    return '';
  }, []);

  const activeSpoke = useMemo(() => {
    if (!activeSpokeId) return null;
    const live = renderState.visibleSpokes.find(s => String(s.veId) === String(activeSpokeId));
    if (!live) return null;
    // Merge SF address fields from the spoke facility record (render-state
    // only carries animation fields like count / lat / lng).
    const meta = displaySpokesAll.find(s => String(s.veId) === String(activeSpokeId));
    return meta ? { ...meta, ...live, name: live.name || meta.name } : live;
  }, [activeSpokeId, renderState, displaySpokesAll]);
  const activeHub = useMemo(() => {
    if (!activeHubId) return null;
    return displayHubs.find(h => String(h.veId) === String(activeHubId)) || null;
  }, [activeHubId, displayHubs]);

  // ---- Marker icons ---------------------------------------------------
  const hubIcon = useMemo(() => {
    if (!mapsLoaded || !window.google?.maps) return undefined;
    // SVG hospital-cross icon so hubs read as "hospitals", not just dots.
    return {
      path: 'M -14 -6 L -6 -6 L -6 -14 L 6 -14 L 6 -6 L 14 -6 L 14 6 L 6 6 L 6 14 L -6 14 L -6 6 L -14 6 z',
      fillColor: '#0f766e',
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 2,
      scale: 1,
      anchor: new window.google.maps.Point(0, 0),
    };
  }, [mapsLoaded]);

  // 0..1 intensity of the "pop" flash for a spoke at the current playhead,
  // based on when its most recent referral landed (lastT).
  const popIntensityFor = useCallback((spoke) => {
    // Pop is a transient playback effect only. When paused / scrubbed /
    // finished, always show the true colormap color — never leave a spoke
    // "stuck" on the magenta flash (high-volume spokes otherwise look pink
    // forever because their lastT stays inside the pop window).
    if (!popOnHit || !isPlaying || !timeline || spoke?.lastT == null) return 0;
    if (playhead >= 0.999) return 0;
    const rangeMs = Math.max(1, timeline.endMs - timeline.startMs);
    const popWindow = Math.min(0.08, Math.max(0.008, POP_DURATION_MS / animationDurationMs));
    const hitPlayhead = (spoke.lastT - timeline.startMs) / rangeMs;
    const raw = (playhead - hitPlayhead) / popWindow;
    if (raw < 0 || raw >= 1) return 0;
    // Hold full pulse briefly, then ease out back to the heatmap color.
    const hold = 0.15;
    if (raw <= hold) return 1;
    const fade = 1 - (raw - hold) / (1 - hold);
    return fade * fade;
  }, [popOnHit, isPlaying, timeline, animationDurationMs, playhead]);

  const iconForSpoke = useCallback((spoke) => {
    if (!mapsLoaded || !window.google?.maps) return undefined;
    // Inbound (default) = filled circle. Outbound = filled diamond.
    // Both = keep the spoke event's own direction so the mixed view
    // stays distinguishable. Neutral border ties both shapes together.
    //
    // Non-VE hook (spoke.source === 'non-ve'): when the CTO exposes
    // rpt_non_vital_physicians we'll emit spoke events with
    // source === 'non-ve' and switch to a third shape (e.g. triangle)
    // here so the map still shows direction + repeat-count colormap.
    //
    // Pop = temporary size pulse + brief blend toward a standout color.
    // Fill always starts from the true colormap sample so busy spokes
    // still read as "hot" (red) rather than getting stuck in the flash.
    const pop = popIntensityFor(spoke);
    // Size uses the same intensity curve as color (vs absolute sqrt), so
    // a "large" circle is also a warm one — they can't drift apart.
    const baseSize = spokeMarkerSize(spoke.count, colormapMax, { minSize: 7, maxSize: 26 });
    const size = baseSize * (1 + 0.9 * pop);
    const baseFill = spokeColorForCount(spoke.count);
    // Blend toward standout pink, but never fully replace the heatmap
    // color — when the pulse ends, fill is exactly baseFill again.
    const fill = pop > 0.02 ? lerpHex(baseFill, POP_COLOR, Math.min(0.85, pop * 0.85)) : baseFill;
    const stroke = pop > 0.05 ? POP_STROKE : '#111827';
    const strokeWeight = pop > 0.05 ? 2.75 : 1.5;
    const fillOpacity = 0.95;
    if (spoke.source === 'non-ve') {
      // Placeholder triangle for the future non-VE layer. No records
      // currently carry this source, but the icon path is here so the
      // day the API lands the visual is already differentiable.
      return {
        path: 'M 0 -10 L 9 8 L -9 8 z',
        fillColor: fill,
        fillOpacity,
        strokeColor: stroke,
        strokeWeight,
        scale: size / 10,
      };
    }
    if (spoke.direction === DIRECTION_OUTBOUND) {
      // Diamond via SymbolPath rotation.
      return {
        path: 'M 0 -10 L 10 0 L 0 10 L -10 0 z',
        fillColor: fill,
        fillOpacity,
        strokeColor: stroke,
        strokeWeight,
        scale: size / 10,
      };
    }
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: fill,
      fillOpacity,
      strokeColor: stroke,
      strokeWeight,
      scale: size / 2,
    };
  }, [mapsLoaded, spokeColorForCount, popIntensityFor, colormapMax]);

  // ---- Playback controls ----------------------------------------------
  const togglePlay = () => {
    if (!timeline) return;
    if (playhead >= 1) setPlayhead(0);
    setIsPlaying(p => !p);
  };
  const restart = () => {
    setIsPlaying(false);
    setPlayhead(0);
  };
  const scrub = (v) => {
    setIsPlaying(false);
    setPlayhead(Number(v));
  };

  // ---- Export MP4 -----------------------------------------------------
  const handleExport = async () => {
    if (!timeline || eventsWithCoords.length === 0) return;
    setExporting(true);
    setExportProgress(0);
    setExportMessage('Preparing export…');
    try {
      // Capture the live map camera so the video matches the user's
      // current pan/zoom, not an auto-fit of every spoke nationwide.
      let viewport = null;
      const map = mapRef.current;
      if (map && typeof map.getCenter === 'function' && typeof map.getZoom === 'function') {
        const c = map.getCenter();
        const z = map.getZoom();
        if (c && Number.isFinite(z)) {
          viewport = { center: { lat: c.lat(), lng: c.lng() }, zoom: z };
        }
      }
      const filenameStem = `spoke-growth_${startDate}_to_${endDate}`;
      await exportSpokeGrowthMp4({
        timeline: { ...timeline, events: eventsWithCoords },
        hubs: displayHubs,
        colormapId,
        durationMs: animationDurationMs,
        filenameStem,
        mapStyleId,
        lineEffect,
        lineColorMode,
        customLineColor,
        popOnHit,
        viewport,
        onProgress: (p, msg) => {
          setExportProgress(p);
          if (msg) setExportMessage(msg);
        },
      });
      setExportMessage('Export complete.');
    } catch (e) {
      setExportMessage(`Export failed: ${e?.message || 'unknown error'}`);
    } finally {
      setExporting(false);
      setTimeout(() => { setExportProgress(null); setExportMessage(''); }, 4000);
    }
  };

  // ---- Render ---------------------------------------------------------
  if (!MAP_KEY) {
    return (
      <div className="spoke-growth-empty">
        <strong>Map unavailable.</strong>
        <span><code>REACT_APP_GOOGLE_MAPS_API_KEY</code> is not configured on this build.</span>
      </div>
    );
  }

  return (
    <div className="spoke-growth">
      <div className="spoke-controls">
        <div className="spoke-controls-row">
          <label className="spoke-field">
            <span>Scope</span>
            <select value={scopeKind} onChange={e => setScopeKind(e.target.value)}>
              {SCOPE_KINDS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>

          {scopeKind === 'customer' && (
            <label className="spoke-field spoke-field-grow">
              <span>Customer</span>
              <select value={customerId} onChange={e => setCustomerId(e.target.value)}>
                <option value="">— pick a customer —</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>
                    {c._depth ? `\u00A0\u00A0\u00A0\u00A0${c.name}` : c.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {scopeKind === 'filter' && (
            <label className="spoke-field spoke-field-grow">
              <span>Saved Filter</span>
              <select value={filterId} onChange={e => setFilterId(e.target.value)}>
                <option value="">— pick a filter —</option>
                {savedFilters.map(sf => (
                  <option key={sf.id} value={sf.id}>{sf.name}</option>
                ))}
              </select>
            </label>
          )}
          {scopeKind === 'facility' && (
            <label className="spoke-field spoke-field-grow">
              <span>Facility</span>
              <div className="spoke-facility-picker">
                <input
                  type="text"
                  placeholder="Search by name, city, state, or VE ID…"
                  value={facilitySearch}
                  onChange={e => setFacilitySearch(e.target.value)}
                />
                <select
                  value={singleFacilityId}
                  onChange={e => setSingleFacilityId(e.target.value)}
                  size={Math.min(6, Math.max(3, facilityPickerRows.length || 1))}
                >
                  {facilityPickerRows.length === 0 && (
                    <option value="" disabled>No facilities match</option>
                  )}
                  {facilityPickerRows.map(f => (
                    <option key={f.veId} value={f.veId}>
                      {f.name}{f.city ? ` — ${f.city}${f.state ? `, ${f.state}` : ''}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </label>
          )}
        </div>

        <div className="spoke-controls-row">
          <label className="spoke-field">
            <span>Start date</span>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </label>
          <label className="spoke-field">
            <span>End date</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </label>
          <label className="spoke-field">
            <span>Direction</span>
            <select value={direction} onChange={e => setDirection(e.target.value)}>
              {DIRECTION_OPTIONS_LOCAL.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="spoke-field">
            <span>Speed</span>
            <select value={speedId} onChange={e => setSpeedId(e.target.value)}>
              {SPEED_OPTIONS.map(o => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="spoke-field">
            <span>Colormap</span>
            <select value={colormapId} onChange={e => setColormapId(e.target.value)}>
              {COLORMAPS.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
          <label className="spoke-field">
            <span>Map style</span>
            <select value={mapStyleId} onChange={e => setMapStyleId(e.target.value)}>
              {MAP_STYLES.map(m => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>
          </label>
          <label className="spoke-field">
            <span>Line effect</span>
            <select value={lineEffect} onChange={e => setLineEffect(e.target.value)}>
              {LINE_EFFECTS.map(o => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="spoke-field">
            <span>Line color</span>
            <select
              value={lineColorMode}
              onChange={e => setLineColorMode(e.target.value)}
              disabled={lineEffect === 'icons'}
            >
              {LINE_COLOR_MODES.map(o => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </label>
          {lineColorMode === 'custom' && lineEffect !== 'icons' && (
            <label className="spoke-field spoke-field-color">
              <span>Custom line</span>
              <input
                type="color"
                value={customLineColor}
                onChange={e => setCustomLineColor(e.target.value)}
                aria-label="Custom spoke line color"
              />
            </label>
          )}
          <label className="spoke-checkbox">
            <input
              type="checkbox"
              checked={popOnHit}
              onChange={e => setPopOnHit(e.target.checked)}
            />
            <span>Pop on new referral</span>
          </label>
          <label className="spoke-checkbox">
            <input
              type="checkbox"
              checked={removeTestData}
              onChange={e => setRemoveTestData(e.target.checked)}
            />
            <span>Exclude test data</span>
          </label>
          <button
            type="button"
            className="spoke-primary-btn"
            onClick={buildTimeline}
            disabled={!canBuild || loading}
          >
            {loading ? 'Building…' : 'Build timeline'}
          </button>
        </div>

        {loadError && (
          <div className="spoke-error">{loadError}</div>
        )}
        {!canBuild && !loadError && (
          <div className="spoke-hint">
            Pick a scope (customer, saved filter, or single facility) and a
            date range, then click <strong>Build timeline</strong>.
          </div>
        )}
      </div>

      {hasBuilt && timeline && (
        <>
          <div className="spoke-summary">
            <div className="spoke-summary-item">
              <span className="spoke-summary-label">Hubs</span>
              <span className="spoke-summary-value">{displayHubs.length}</span>
            </div>
            <div className="spoke-summary-item">
              <span className="spoke-summary-label">Spokes</span>
              <span className="spoke-summary-value">{displaySpokesAll.length}</span>
            </div>
            <div className="spoke-summary-item">
              <span className="spoke-summary-label">Referrals in scope</span>
              <span className="spoke-summary-value">
                {(timeline.stats?.totalInScope ?? eventsWithCoords.length).toLocaleString()}
              </span>
            </div>
            <div className="spoke-summary-item">
              <span className="spoke-summary-label">On map (external)</span>
              <span className="spoke-summary-value">{eventsWithCoords.length.toLocaleString()}</span>
            </div>
            <div className="spoke-summary-item">
              <span className="spoke-summary-label">Direction</span>
              <span className="spoke-summary-value">
                {DIRECTION_OPTIONS_LOCAL.find(o => o.value === direction)?.label || direction}
              </span>
            </div>
            {(timeline.stats?.internalCount > 0) && (
              <div className="spoke-summary-item spoke-summary-warn">
                <em>
                  {(timeline.stats.internalCount).toLocaleString()} hub↔hub
                  {' '}(internal) — counted above, not plotted as spokes
                </em>
              </div>
            )}
            {(geocoding || hubsMissingCoords.length + spokesMissingCoords.length > 0) && (
              <div className="spoke-summary-item spoke-summary-warn">
                {geocoding ? (
                  <em>Geocoding missing addresses…</em>
                ) : (
                  <>
                    <em>
                      {hubsMissingCoords.filter(f => !resolvedCoords[f.veId]).length} hub /
                      {' '}
                      {spokesMissingCoords.filter(f => !resolvedCoords[f.veId]).length} spoke
                      {' '}without coordinates
                    </em>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="spoke-playback">
            <button
              type="button"
              className="spoke-play-btn"
              onClick={togglePlay}
              disabled={eventsWithCoords.length === 0}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? '❚❚ Pause' : '► Play'}
            </button>
            <button
              type="button"
              className="spoke-play-btn"
              onClick={restart}
              disabled={eventsWithCoords.length === 0}
            >
              ↺ Restart
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.001}
              value={playhead}
              onChange={e => scrub(e.target.value)}
              disabled={eventsWithCoords.length === 0}
              className="spoke-slider"
              aria-label="Timeline scrubber"
            />
            <div className="spoke-timeline-label">
              <span>{fmtDateLabel(startDate)}</span>
              <strong>{currentTimestampLabel}</strong>
              <span>{fmtDateLabel(endDate)}</span>
            </div>
            <button
              type="button"
              className="spoke-export-btn"
              onClick={handleExport}
              disabled={exporting || eventsWithCoords.length === 0}
              title="Render the animation to an MP4 using your current map pan/zoom, then download."
            >
              {exporting ? 'Exporting…' : 'Export MP4'}
            </button>
          </div>

          {exporting && (
            <div className="spoke-export-progress">
              <div
                className="spoke-export-progress-bar"
                style={{ width: `${Math.max(0, Math.min(100, Math.round((exportProgress || 0) * 100)))}%` }}
              />
              <span>{exportMessage || 'Rendering…'}</span>
            </div>
          )}
          {!exporting && exportMessage && (
            <div className="spoke-export-progress">
              <span>{exportMessage}</span>
            </div>
          )}

          <div className={`spoke-map-wrap spoke-map-wrap-${mapStyleId}`}>
            {mapsLoadError && (
              <div className="spoke-growth-empty">
                <strong>Map failed to load</strong>
                <span>Check that the Maps JavaScript API is enabled on the Google Cloud project.</span>
              </div>
            )}
            {!mapsLoadError && !mapsLoaded && (
              <div className="spoke-growth-empty">Loading map…</div>
            )}
            {!mapsLoadError && mapsLoaded && (
              <GoogleMap
                mapContainerStyle={MAP_CONTAINER}
                center={DEFAULT_CENTER}
                zoom={DEFAULT_ZOOM}
                onLoad={(m) => { mapRef.current = m; setMapReady(true); }}
                onUnmount={() => { mapRef.current = null; setMapReady(false); }}
                mapTypeId={activeMapStyle.mapTypeId}
                options={{
                  mapTypeControl: false,
                  streetViewControl: false,
                  fullscreenControl: true,
                  styles: activeMapStyle.styles,
                  // Backing color that shows through when features are
                  // hidden. Keeps "Blank" from looking like a broken tile.
                  backgroundColor: mapStyleId === 'dark' ? '#111827' : '#f8fafc',
                }}
              >
                {connectionLines.map(line => (
                  <PolylineF
                    key={line.key}
                    path={line.path}
                    options={{
                      strokeColor: line.color,
                      strokeOpacity: line.opacity ?? 0.55,
                      strokeWeight: 1 + Math.min(3, Math.sqrt(line.count)),
                      geodesic: false,
                      clickable: false,
                      zIndex: 1,
                    }}
                  />
                ))}
                {renderState.visibleSpokes.map(sp => (
                  <MarkerF
                    key={`spoke-${sp.veId}-${sp.direction}`}
                    position={{ lat: sp.lat, lng: sp.lng }}
                    icon={iconForSpoke(sp)}
                    onClick={() => {
                      setActiveHubId(null);
                      setActiveSpokeId(sp.veId);
                    }}
                  />
                ))}
                {displayHubs.map(h => (
                  <MarkerF
                    key={`hub-${h.veId}`}
                    position={{ lat: h.lat, lng: h.lng }}
                    icon={hubIcon}
                    zIndex={999}
                    onClick={() => {
                      setActiveSpokeId(null);
                      setActiveHubId(h.veId);
                    }}
                  />
                ))}
                {activeHub && (
                  <InfoWindow
                    position={{ lat: activeHub.lat, lng: activeHub.lng }}
                    onCloseClick={() => setActiveHubId(null)}
                  >
                    <div className="spoke-info">
                      <div className="spoke-info-tag spoke-info-hub">HUB</div>
                      <div className="spoke-info-name">{activeHub.name}</div>
                      <div className="spoke-info-addr">
                        {formatFacilityAddress(activeHub) || '(no address on file)'}
                      </div>
                      <div className="spoke-info-count">VE ID {activeHub.veId}</div>
                    </div>
                  </InfoWindow>
                )}
                {activeSpoke && (
                  <InfoWindow
                    position={{ lat: activeSpoke.lat, lng: activeSpoke.lng }}
                    onCloseClick={() => setActiveSpokeId(null)}
                  >
                    <div className="spoke-info">
                      <div className={`spoke-info-tag spoke-info-${activeSpoke.direction}`}>
                        {activeSpoke.direction === DIRECTION_OUTBOUND ? 'OUTBOUND SPOKE' : 'INBOUND SPOKE'}
                      </div>
                      <div className="spoke-info-name">{activeSpoke.name}</div>
                      {formatFacilityAddress(activeSpoke) && (
                        <div className="spoke-info-addr">{formatFacilityAddress(activeSpoke)}</div>
                      )}
                      <div className="spoke-info-count">
                        <strong>{activeSpoke.count.toLocaleString()}</strong>
                        {' '}referral{activeSpoke.count === 1 ? '' : 's'} so far
                      </div>
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default SpokeGrowth;
